// ----------      START   IMPORT  BLOCK        ----------
import { useState } from "react";
import BaseLayout from "../../General/LandingPage/BaseLayout/BaseLayout";
import { Container, ProgressBar, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import StepWelcome from "./Steps/StepWelcome"; 
import StepBasics from "../ProfileOnboarding/Steps/StepBasics";
import ContactBasics from "../ProfileOnboarding/Steps/StepContact";
import StepWorkingHours from "./Steps/StepWorkingHours";
import StepQualifications from "./Steps/StepQualifications";
import StepSkills from "./Steps/StepSkills";
import StepWorkLocation from "./Steps/StepWorkLocation";
import { useNavigate } from "react-router-dom";

import UserProOnboardingService from "../../../../API/Services/UserProOnboardingService";
import { useLanguage } from "../../../../API/Context/LanguageContext";
import { useUser } from "../../../../API/Context/UserContext";

import { RelationshipsEnum } from "../../../../Types/Enums/RelationshipsEnum";
import { VocabProOnboarding as Vocab } from "../../../../ConfigFiles/multilingual/VocabProOnboarding";
import { VocabButtons } from "../../../../ConfigFiles/multilingual/VocabButtons";
import { FRONTEND_ROUTES } from "../../../../ConfigFiles/Routes/frontend_routes";
import type { UserRead } from "../../../../Types/schema-helpers";
import { BloodTypesEnum } from "../../../../Types/Enums/BloodTypesEnum";
// ----------      END   IMPORT  BLOCK        ----------

const ProOnboarding = () => {
  const { lan } = useLanguage();
  const { setProfile } = useUser();
  const navigate = useNavigate();

  const totalSteps = 6;
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Metadata cache for UI selectors
  const [uiMetadata, setUiMetadata] = useState({
    skills: {},
    specialties: {},
    subspecialties: {},
    states: {},
  });

  const updateMetadata = (category: string, newMeta: any) => {
    setUiMetadata((prev: any) => ({
      ...prev,
      [category]: { ...(prev[category] || {}), ...newMeta },
    }));
  };

  const [formData, setFormData] = useState({
    // 1. Immutables
    birthday: "", 
    blood_type: BloodTypesEnum.OP,
    biological_sex: 0, 
    sex: 0,
    birthplace: "",

    // 2. Contact/Addresses
    phone_numbers: [""], 
    mails: [""],
    current_address: {
      street: "",
      number: "",
      interior_number: null,
      city: "",
      state_id: "",
      state: "",             
      zip_code: "",
      country: "Mexico",
      google_maps_url: ""
    },
    emergency_contacts: [{ name: "", phone: "", relationship: RelationshipsEnum.PARENT}],

    // 3. Work Location
    professional_work_location: {
      location_name: "Consultorio Principal",
      street: "",
      number: "",
      zip_code: "",
      country_code: "MX",
      state_id: null,
      city_id: null, 
      location: { type: "Point", coordinates: [0, 0] },
      phone: ""
    },
    professional_assignment: {
      interior_number: null,
      work_number: "",
      work_extension: null,
    },

    // 4. Expertise
    skills: {
      specialty_id: null,
      subspecialty_id: null,
      skills: []
    },

    // 5. Qualifications
    qualifications: [
      {
        id: crypto.randomUUID(),
        title: "",
        institution: "",
        license: "",
        year_of_completion: new Date().getFullYear(),
        deleted: false
      }
    ],

    // 6. Agenda logic
    working_hours: {
      weekly_schedule: [[], [], [], [], [], [], []], 
      timezone: "America/Mexico_City",
      manage_agenda: true,
      schedule_visible: true
    }
  });

  const updateFormData = (newData: any) => setFormData(prev => ({ ...prev, ...newData }));

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);

      const service = new UserProOnboardingService();
      
      // Submit onboarding and acquire updated UserRead directly from backend
      const updatedUserRead: UserRead = await service.createProOnboarding(formData);

      if (updatedUserRead) {
        // Hydrate React UserContext session directly with the returned server payload
        setProfile(updatedUserRead);

        // Navigate to success landing / verification pending screen
        navigate(FRONTEND_ROUTES.onboarding_success, { 
          state: { flow: "PURE_PROFESSIONAL" },
          replace: true
        });
      }
    } catch (error: any) {
      console.error("Failed to submit onboarding:", error);
      setErrorMessage(
        error.response?.data?.detail || "An unexpected error occurred during onboarding submit."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <Container className="py-5 onboarding-container">
        {/* --- HEADER SECTION --- */}
        <div className="text-center mb-4">
          <h2 className="fw-bold text-primary">{Vocab.pro_onboarding_title[lan]}</h2>
          <p className="text-muted mb-2">{Vocab.pro_onboarding_message[lan]}</p>
          
          <div className="d-flex align-items-center justify-content-center gap-3">
            <ProgressBar 
              now={(step / totalSteps) * 100} 
              style={{ height: "10px", width: "100%", maxWidth: "400px" }} 
              className="rounded-pill shadow-sm"
            />
            <Badge bg="primary" className="rounded-pill px-3 py-2">
              {step} / {totalSteps}
            </Badge>
          </div>
        </div>

        {errorMessage && (
          <Alert variant="danger" dismissible onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        <Card className="shadow-lg border-0 rounded-4">
          <Card.Body className="p-4 p-md-5">
            {step === 0 && <StepWelcome />}
            {step === 1 && <StepBasics data={formData} update={updateFormData} isImmutable={true}/>}
            {step === 2 && <StepQualifications data={formData} update={updateFormData}/>}
            {step === 3 && <ContactBasics data={formData} update={updateFormData} />}
            {step === 4 && <StepWorkLocation data={formData} update={updateFormData} metadata={uiMetadata} updateMeta={updateMetadata}/>}
            {step === 5 && <StepWorkingHours data={formData} update={updateFormData} />}
            {step === 6 && <StepSkills data={formData} update={updateFormData} metadata={uiMetadata} updateMeta={updateMetadata}/>}

            <div className="d-flex justify-content-between mt-5 pt-3 border-top">
              <Button 
                variant="outline-secondary" 
                size="lg" 
                onClick={() => setStep(s => s - 1)} 
                disabled={step === 0 || isSubmitting}
              >
                {step === 0 ? "---" : VocabButtons.back[lan]}
              </Button>
              
              <Button 
                variant="primary" 
                size="lg" 
                disabled={isSubmitting}
                onClick={() => (step === totalSteps ? handleSubmit() : setStep(s => s + 1))}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Submitting...
                  </>
                ) : step === 0 ? (
                  VocabButtons.start[lan]
                ) : step === totalSteps ? (
                  VocabButtons.submit[lan]
                ) : (
                  VocabButtons.next[lan]
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </BaseLayout>
  );
};

export default ProOnboarding;